import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    Typography,
    Button,
    Grid,
    Tooltip,
    Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { formatDate } from "../../../utils/formatDate.utils";
import { useParams } from "react-router-dom";
import { apiGetAprendaStats } from "../../../services/apiGetAprendaStats";
import { Loader } from "../../../components/ui/Loader";

export const AprendaStatsTab = () => {
    const { id: comunidadeId } = useParams();
    const [aprendaStats, setAprendaStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isUpdated, setIsUpdated] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const fetchData = async () => {
        try {
            setLoading(true);
            setIsUpdated(false);

            const response = await apiGetAprendaStats(comunidadeId);
            setAprendaStats(response);
            setIsUpdated(true);
        } catch (error) {
            console.error("Erro ao buscar dados dos aprendas:", error);
            setAprendaStats(null);
            setIsUpdated(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [comunidadeId]);

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        if (!aprendaStats?.estatisticasPorAprenda || !Array.isArray(aprendaStats.estatisticasPorAprenda)) return [];

        let sortedItems = [...aprendaStats.estatisticasPorAprenda];
        if (sortConfig.key) {
            sortedItems.sort((a, b) => {
                if (!a || !b) return 0;

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (valA < valB) {
                    return sortConfig.direction === "asc" ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === "asc" ? 1 : -1;
                }
                return 0;
            });
        }
        return sortedItems;
    }, [aprendaStats?.estatisticasPorAprenda, sortConfig]);

    if (!isUpdated) {
        return <Loader />;
    }

    return (
        <Box pb={4}>
            {aprendaStats?.resumoGeral && (
                <Card sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Resumo Geral
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {aprendaStats.resumoGeral.totalAprendas}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total de Vídeos
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {aprendaStats.resumoGeral.acessosUnicos}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Acessos Únicos
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {aprendaStats.resumoGeral.totalAcessos}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total de Acessos
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {aprendaStats.resumoGeral.percentualCompletudeMedia?.toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completude Média
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>
            )}

            <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6">Dados sobre Trilha de Aprenda</Typography>
                    <Tooltip title="Exportar para Excel">
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => exportToExcel(aprendaStats, sortedData)}
                            sx={{
                                color: "var(--primary-color)",
                                borderColor: "var(--primary-color)",
                                "&:hover": {
                                    borderColor: "var(--primary-color-dark)",
                                    backgroundColor: "rgba(var(--primary-color-rgb), 0.04)",
                                    transform: "scale(1.02)",
                                    transition: "all 0.2s",
                                },
                            }}
                        >
                            Exportar
                        </Button>
                    </Tooltip>
                </Box>

                <TableContainer component={Paper} sx={{ boxShadow: "none", overflow: "auto" }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell onClick={() => handleSort("titulo")} style={{ cursor: "pointer" }}>
                                    Título
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalAcessos")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Total Acessos
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("creatorsUnicos")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Usuários Únicos
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("tempoTotalAssistido")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Tempo Assistido (min)
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("percentualConclusao")}
                                    style={{ cursor: "pointer" }}
                                >
                                    % Conclusão
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("ultimoAcesso")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Último Acesso
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.titulo}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.totalAcessos}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.creatorsUnicos}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {Math.round(item.tempoTotalAssistido / 60)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        <Chip
                                            label={`${item.percentualConclusao.toFixed(1)}%`}
                                            color={
                                                item.percentualConclusao >= 80
                                                    ? "success"
                                                    : item.percentualConclusao >= 50
                                                    ? "warning"
                                                    : "error"
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.ultimoAcesso ? formatDate(item.ultimoAcesso) : "Nunca"}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sortedData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        {loading ? "Carregando..." : "Nenhum dado encontrado"}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {aprendaStats?.topCreators && aprendaStats.topCreators.length > 0 && (
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Top Usuários
                    </Typography>
                    <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome</TableCell>
                                    <TableCell align="center">Total Acessos</TableCell>
                                    <TableCell align="center">Tempo Assistido (min)</TableCell>
                                    <TableCell align="center">Aprendas Acessados</TableCell>
                                    <TableCell align="center">% Progresso</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {aprendaStats.topCreators.map((creator, index) => (
                                    <TableRow key={creator.idcreator}>
                                        <TableCell>{creator.nome || creator.idcreator}</TableCell>
                                        <TableCell align="center">{creator.totalAcessos}</TableCell>
                                        <TableCell align="center">{Math.round(creator.tempoTotalAssistido / 60)}</TableCell>
                                        <TableCell align="center">{creator.aprendasAcessados}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={`${creator.percentualProgresso.toFixed(1)}%`}
                                                color={
                                                    creator.percentualProgresso >= 80
                                                        ? "success"
                                                        : creator.percentualProgresso >= 50
                                                        ? "warning"
                                                        : "error"
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}
        </Box>
    );
};

    const exportToExcel = (aprendaStats, sortedData) => {
        if (!aprendaStats?.estatisticasPorAprenda) return;

        const headers = [
            "Título",
            "Total Acessos",
            "Usuários Únicos",
            "Tempo Total Assistido (min)",
            "Percentual Conclusão (%)",
            "Último Acesso",
        ];

        const data = sortedData.map((item) => [
            item.titulo,
            item.totalAcessos,
            item.creatorsUnicos,
            Math.round(item.tempoTotalAssistido / 60),
            item.percentualConclusao.toFixed(2),
            item.ultimoAcesso ? formatDate(item.ultimoAcesso) : "Nunca",
        ]);

        const worksheetData = [headers, ...data];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Aprenda");
        
        XLSX.writeFile(
            workbook,
            `aprenda_stats_${new Date().toISOString().split("T")[0]}.xlsx`
        );
    };
